import { Col, Row } from "~/common/components/Layout";
import NullableImage from "~/common/components/NullableImage";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  OldDesignationParameter,
  SchaefflerIdParameter,
  TariffParameter,
} from "~/domains/product/components/ProductParams";
import { Link } from "~/common/components/Link";
import { PredefinedTraits } from "~/features/productDetails/components/ProductTrait";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { getIsContractAvailable, isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { type MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectIsCartAmountsLoaded } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { type MessageType } from "~/common/components/Messages/Message";
import { addMessage } from "~/features/toasts/redux/actions";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/actions";
import { type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  usePdpSummaryLogic,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { getPriceInfo, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { selectPriceScalingIndex } from "~/utils/pricePicker";
import { isOfferPriceValid } from "~/utils/packageEntryUtils";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import { sendAddToQuote } from "~/features/cart/activeQuote/api/addToQuote";
import SalesmanDetailsToggle from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/SalesmanDetailsToggle";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import PdpPrice from "~/features/productDetails/components/PdpPrice";
import PriceInput from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/PriceInput";
import SimplePriceText, { PriceTextVariants } from "~/domains/pricing/components/SimplePriceText/SimplePriceText";
import {
  obtainSpecialPriceLabel,
  Price,
  PriceType,
  type ProductOtherPriceProps,
} from "~/domains/pricing/components/Price/v1/Price";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { CURRENCY_ISO, MAX_QUANTITY } from "~/utils/constants";
import { canSeeListPrice } from "~/utils/features";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import SalesmanPriceTable from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/SalesmanPriceTable";
import { PREFERRED_PRODUCTS_ID } from "~/features/productDetails/constants";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { Card } from "~/features/configurator/Linear/components/Card";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import classesExtended from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary.module.less";
import CenterContents from "~/common/components/CenterContents";
import { Button, Stack, styled, Typography } from "~/common/mui";
import { PackagingSelection } from "~/domains/product/components/PackagingSelection/PackagingSelection";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import type { AddToCartFormData } from "~/features/addToCart/hooks/useAddToCartNew";
import { useFormContext } from "react-hook-form";
import {
  getPurchaseDataFromUrl,
  selectAmountInCart,
} from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import type { NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import type { SourceInfo } from "react-number-format";
import { Alert } from "~/common/mui/Alert/Alert";
import { InfoOrderHandlingUnitQuantity } from "~/features/productDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/features/productDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/features/productDetails/components/usePDPTabs";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/mui/icons/Cycle";
import parse from "html-react-parser";
import ProductMMD from "~/domains/product/components/ProductMMD";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import Tag from "~/common/components/Tag/Tag";
import { usePdpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/usePdpCmnLogic";
import { ContactSalesTeamLink } from "~/features/productDetails/components/ProductSummary/common/PdpLinks";
import { Product } from "~/domains/product/Product";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";
import { VariantSelect } from "./components/VariantSelect";
import { useVariantContext } from "../../context/VariantContext";

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

interface AddToCartCardState {
  offerPrice?: PriceData;
  targetPrice?: PriceData;

  showDetails: boolean;
  priceOverriden: boolean;

  sending: boolean;
}

const isSalesman = userHasPermission(Permission.ADD_TO_QUOTE);
const isPublic = userIsPublic() || userIsRegistered();

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for Salesmen
 */
export const SalesmanPdpSummary = ({ productDescriptor, hasPreferredProducts, changeTab }: Props) => {
  const { t } = useTranslation();
  const reco_place = usePurchasePlace();
  const displayMMDAssistant = useDisplayMMDAssistant();

  const formProps = useFormContext<AddToCartFormData>();

  const product = productDescriptor.base;

  const primaryImage = product.images.filter(
    (imgData) => imgData.imageType === "PRIMARY" && imgData.format === "product",
  )[0];

  const { manufacturerLogoUrl, brandLogoUrl } = product;

  const isContractProduct = getIsContractAvailable(productDescriptor);

  const selectedPackageCode = formProps.watch("selectedPackCode");
  const onSelectPackage = (packCode: string) => formProps.setValue("selectedPackCode", packCode);
  const { variantLoading } = useVariantContext();

  const quantity = formProps.watch("quantity");
  const priceReference = formProps.watch("priceReference");

  const [state, setState] = React.useState<AddToCartCardState>({
    showDetails: false,
    priceOverriden: false,
    sending: false,
  });

  const dispatch = useDispatch();
  const addMessageCb = (type: MessageType, label: string) => dispatch(addMessage(type, label));
  const onAddToQuoteSuccess = (qty: number, packCode: string) => dispatch(addQuantity(product.code, packCode, qty, ""));

  const webServicesDisabled = window.SCHA.config.disableAllWS;

  const availabilityInfo = productDescriptor.availability;
  const isLoading = productDescriptor.isLoading;
  const isError = productDescriptor.isError && !webServicesDisabled;

  const productFamilyName = product.preferredProductsFamilyName;
  const isExternalProduct = product.externalProduct;
  const quantityVisible = product.quantityVisible && !product.externalProduct;

  const { priceOverriden, showDetails } = state;
  const packagingInformation = availabilityInfo?.packagingInformation ?? [];
  const selectedPackage: PackagingInformationEntry | undefined = packagingInformation.find(
    (pack) => pack.packCode === selectedPackageCode,
  );
  const packSize = selectedPackage?.packSize ?? 1;
  const packCode = selectedPackage?.packCode ?? "";

  const qtyInCart = useSelector(selectAmountInCart({ product: productDescriptor, packCode }));
  const isCartLoaded = useSelector(selectIsCartAmountsLoaded);

  const minOrderQuantity = findMinOrderQuantity(productDescriptor, packCode);

  const {
    buttons: { showWishlistButton },
    flags: { isPackageSizeInvalid, MOQHelperLabel, showManufacturerLogo },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode,
    packSize,
    // isOnSale: hasSales,
  });

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode });

  const isAddToCartEnabled =
    isProductPMA(productDescriptor) ||
    (priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging);

  const qtyInputEnabled = !isLoading && (!isError || showWishlistButton) && product.quantityVisible;

  const wsError = productDescriptor.isError;
  const addToCartDisabled = !isAddToCartEnabled || isPackageSizeInvalid || !selectedPackage || wsError;

  const nettoPrice = formProps.watch("netPrice");
  const priceUnit = formProps.watch("priceUnit");

  const offerPrice = !priceOverriden ? nettoPrice : state.offerPrice;

  const targetPrice = state.targetPrice;

  let isInvalid = false;

  if (showDetails && priceInfo) {
    const priceScalingIndex = selectPriceScalingIndex(quantity + qtyInCart, priceInfo.priceScale);
    let msp = undefined;
    if (priceInfo) {
      const scalingEntry = priceInfo.priceScaleMSP[priceScalingIndex];
      msp = scalingEntry ? scalingEntry.price : undefined;
    }

    if (msp && priceOverriden && state.offerPrice) {
      isInvalid = !isOfferPriceValid(state.offerPrice, msp);
    }
  }

  const userIsSupposedToSeePrice = userHasPermission(Permission.PRICE);

  const isSpecial = isSpecialPrice(productDescriptor, packCode);

  const toggleDetails = () => setState((prevState) => ({ ...prevState, showDetails: !prevState.showDetails }));
  const updateOfferPrice = (e: NumberFormatEvent, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === "prop") {
      return;
    }
    return setState((prevState) => ({
      ...prevState,
      offerPrice: e
        ? {
            value: e.floatValue ?? 0,
            currencyIso: CURRENCY_ISO,
          }
        : undefined,
      priceOverriden: true,
    }));
  };
  const blurOfferPrice = () => {
    setState((prevState) => ({ ...prevState, priceOverriden: prevState.offerPrice !== undefined }));
  };
  const updateTargetPrice = (e: NumberFormatEvent, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === "prop") {
      return;
    }
    setState((prevState) => ({
      ...prevState,
      targetPrice: e
        ? {
            value: e.floatValue ?? 0,
            currencyIso: CURRENCY_ISO,
          }
        : undefined,
    }));
  };
  const updateQuantity = (quantity: number) =>
    formProps.setValue("quantity", quantity, { shouldTouch: true, shouldDirty: true });

  const openModalWishlist = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    trackPdpWishlistClick(product.designation, 1);

    openAddToWishlistWizard({
      productCode: product.code,
      initialQuantity: formProps.getValues("quantity"),
      initialPriceReference: getInitialPriceReference(),
      addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
    });
  };

  const addToQuote = () => {
    const packageData = selectedPackage!;
    const { priceScale, priceScaleMSP, priceScaleRSP } = priceInfo!;

    const priceScalingIndex = selectPriceScalingIndex(quantity + qtyInCart, priceScale);
    const msp = priceScaleMSP[priceScalingIndex] ? priceScaleMSP[priceScalingIndex].price : undefined;
    const rsp = priceScaleRSP[priceScalingIndex] ? priceScaleRSP[priceScalingIndex].price : undefined;

    setState((prevState) => ({ ...prevState, sending: true }));

    const netPrice = formProps.watch("netPrice");
    const offerPrice = state.offerPrice;

    const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };

    sendAddToQuote(
      {
        quantity: quantity,
        productCode: product.code,
        netPrice: netPrice ? netPrice.value : 0,
        packageCode: packCode,
        packageSize: packageData.packSize,
        primaryWarehouseCode: packageData.primaryWarehouseCode,
        selectedDeliveryDate: new Date(),
        offerPrice: offerPrice ? offerPrice.value : undefined,
        targetPrice: state.targetPrice ? state.targetPrice.value : undefined,
        msp: msp ? msp.value : undefined,
        rsp: rsp ? rsp.value : undefined,
        fifteenDigit: productDescriptor.isMultiPrice || false,
        priceReference,
        priceUnit: priceInfo!.priceUnit,
      },
      context,
    )
      .then(() => {
        onAddToQuoteSuccess(quantity, packCode);
        addMessageCb("info", t("addToQuote.success", { count: quantity }));
        setState((prevState) => ({ ...prevState, sending: false }));
      })
      .catch(() => {
        addMessageCb("error", t("addToQuote.failure", { count: quantity }));
        setState((prevState) => ({ ...prevState, sending: false }));
      });
  };

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: webServicesDisabled || addToCartDisabled || state.sending || variantLoading,
      onClick: addToQuote,
    },
    wishlist: {
      disabled: variantLoading,
      onClick: openModalWishlist,
    },
    configurator: {
      disabled: variantLoading,
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      disabled: variantLoading,
      quantity,
    },
  });

  const mmdCmnData = usePdpCmnLogic();

  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled();

  let qtyHelperLabel =
    isMOQCheckEnabled && minOrderQuantity > 1 ? t("page.moq.helperText.moqNotMet", { minOrderQuantity }) : undefined;
  let qtyIsError = false;
  if (isPackageSizeInvalid) {
    qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : t("addToCartPopup.mismatchedQty");
    qtyIsError = isPackageSizeInvalid;
  }
  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;

  return (
    <Row className={"scha__pdp__product-box"}>
      <Col size={3}>
        <Stack gap={2}>
          <Row className="d-flex">
            <Col size={9}>{primaryImage && <NullableImage data={primaryImage} />}</Col>
            <Col size={3} className="scha__product-image__logos">
              {showManufacturerLogo && <img className={"scha__pdp__manufacturer-logo"} src={manufacturerLogoUrl} />}
              {brandLogoUrl && <img className={"scha__pdp__brand-logo"} src={brandLogoUrl} />}
            </Col>
          </Row>

          <ProductDesignation
            as={"h1"}
            designation={product.designation}
            size={"headline-medium"}
            isPreferred={product.preferred}
            showCopyClipboard
          />

          {product.marketDesignation && <ProductMMD marketDesignation={product.marketDesignation} />}

          <Stack gap={1}>
            <Product.ShortDescriptionV2 component={"h2"} variant="titleMedium" data-test-id={"pdp-short-description"} />
            <Product.DescriptionV2 component={"h3"} variant="titleSmall" data-test-id={"pdp-long-description"} />
          </Stack>

          {productDescriptor.base.reconditionable && (
            <RichTooltip
              content={
                <Typography>{parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}</Typography>
              }
              actions={[
                <Button href={URL_RECONDITIONABLE} variant="text">
                  {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                </Button>,
              ]}
            >
              <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
              </Tag>
            </RichTooltip>
          )}

          <NewVersionMessage baseProduct={product} />

          <Stack gap={2}>
            <OldDesignationParameter product={product} label={t("productDetailPage.oldDesignation")} />
            <SchaefflerIdParameter productDescriptor={productDescriptor} />
            <AliasesParameter product={product} mode={"13-digits"} />
            <TariffParameter product={product} />
            {/* <CountryOfOriginParam product={product} /> */}
            <CountryOfOriginDescriptorParam descriptor={productDescriptor} />
          </Stack>

          {isPublic && <ContactSalesTeamLink product={product} />}

          <Stack gap={1}>
            {product.preferred && <PredefinedTraits.PreferredProduct />}
            {isContractProduct && <PredefinedTraits.ContractProduct />}
            {/*<PredefinedTraits.BoughtBefore/>*/}
          </Stack>
        </Stack>
      </Col>

      <Col size={9} className={"scha__pdp__purchase-card"}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Card disableHover={true}>
            <Row>
              <Col className={classesExtended.packagingColumn} size={7}>
                <div className={classesExtended.packagingArea}>
                  <div className={classesExtended.packagingAreaContent}>
                    {productDescriptor.isAvailabilityLoading && (
                      <CenterContents className={"mb-30 w-100"}>
                        <CircleSpinner />
                      </CenterContents>
                    )}
                    <div hidden={!productDescriptor.availability}>
                      <PackagingSelection
                        selectedCode={selectedPackageCode}
                        onSelectCode={onSelectPackage}
                        product={productDescriptor}
                      />
                    </div>
                  </div>
                </div>
              </Col>
              <Col size={5}>
                <Box>
                  <Stack gap={2}>
                    {!wsError && quantityVisible && (
                      <SalesmanDetailsToggle
                        data-test-id={"pdp-toggle-details"}
                        className={"flex-justify-end"}
                        onCheck={toggleDetails}
                        checked={showDetails}
                      />
                    )}

                    <Row>
                      <Col size={showDetails ? 6 : 12}>
                        {isError && t("hybrisAjaxError")}

                        {(isLoading || !isCartLoaded) && <CircleSpinner />}
                        <Stack gap={2}>
                          {!isLoading && (
                            <>
                              {priceInfo && wsError && (
                                <PdpPrice
                                  price={nettoPrice}
                                  isMultiPrice={false}
                                  priceUnit={priceInfo ? priceInfo.priceUnit : 1}
                                  product={productDescriptor}
                                  quantity={quantity}
                                />
                              )}
                              {!wsError && (
                                <PriceInput
                                  label={
                                    <SimplePriceText
                                      variant={
                                        product.quantityVisible === false
                                          ? PriceTextVariants.monthlySubsriptionStartWithBigLetter
                                          : PriceTextVariants.offer
                                      }
                                      priceUnit={priceUnit}
                                    />
                                  }
                                  data-test-id={"pdp-input-offer-price"}
                                  value={offerPrice?.value}
                                  onChange={updateOfferPrice}
                                  onBlur={blurOfferPrice}
                                  error={isInvalid}
                                  helperLabel={
                                    isSpecial ? (
                                      <div className={"scha__customer-special-price"}>{obtainSpecialPriceLabel(t)}</div>
                                    ) : undefined
                                  }
                                />
                              )}
                            </>
                          )}

                          <VariantSelect />

                          {!isExternalProduct && (
                            <PriceReferenceParam
                              data-test-id={"pdp-price-reference"}
                              priceReference={priceReference}
                              product={product}
                              externalLabel={true}
                              editable={canSeePriceRefColumn()}
                              disabled={variantLoading}
                              onChange={(value) => formProps.setValue("priceReference", value ?? "")}
                            />
                          )}

                          {quantityVisible && (
                            <>
                              <QuantityInput
                                min={minQtyValue}
                                max={MAX_QUANTITY}
                                data-test-id={"pdp-input-quantity"}
                                value={quantity}
                                variant={"compact"}
                                disabled={!qtyInputEnabled || variantLoading}
                                step={packSize}
                                onValueChange={updateQuantity}
                                error={qtyIsError}
                                helperLabel={qtyHelperLabel}
                              />
                              <InfoOrderHandlingUnitQuantity
                                preferredPackQuantity={selectedPackage?.preferredPackQuantity ?? null}
                                packSize={selectedPackage?.packSize ?? 1}
                                quantity={quantity}
                              />
                            </>
                          )}
                        </Stack>
                      </Col>

                      {showDetails && !wsError && (
                        <Col size={6}>
                          <div className={"scha__pdp-salesman__target-price-box"}>
                            <PriceInput
                              label={<SimplePriceText variant={PriceTextVariants.targetPrice} priceUnit={priceUnit} />}
                              value={targetPrice?.value}
                              onChange={updateTargetPrice}
                            />
                            {canSeeListPrice({ product: productDescriptor, isSpecialPrice: isSpecial }) && (
                              <ListPrice price={priceInfo?.listPrice} classes={{ root: "mt-30" }} unit={priceUnit} />
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>

                    {product.isOptime && <OptimeSolutionsMessage />}
                    {showDetails && !isProductPMA(productDescriptor) && (
                      <SalesmanPriceTable product={productDescriptor} selectedPackageCode={packCode} />
                    )}

                    {hasPreferredProducts && (
                      <Link href={"#" + PREFERRED_PRODUCTS_ID}>
                        {t("pdp.preferredProducts.title", { code: productFamilyName })}
                      </Link>
                    )}

                    {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}

                    {!webServicesDisabled && !isLoading && userIsSupposedToSeePrice && productDescriptor.isError && (
                      <Alert severity="error">{t("addProduct.wsError")}</Alert>
                    )}

                    {isProductComparisonCheckboxVisible(product) && !isExternalProduct && (
                      <ProductComparisonCheckbox
                        productCode={productDescriptor.base.code}
                        gaLocation={"Product Detail Page"}
                      />
                    )}

                    <Stack gap={1}>{buttons}</Stack>
                  </Stack>
                </Box>
              </Col>
            </Row>
          </Card>
        </Stack>
      </Col>
    </Row>
  );
};

const ListPrice = (props: Omit<ProductOtherPriceProps, "variant">) => {
  return (
    <Price
      variant={PriceType.ProductListPrice}
      {...props}
      sx={{
        margin: 0,
        ...getFontStyle(FontSize.BodyMedium),
        // Label order -1
        '& [data-test-id="unit"]': {
          order: -1,
        },
        // Price margin top
        '& [data-test-id="value"]': {
          marginTop: "5px",
        },
        ...props.sx,
      }}
    />
  );
};

// Copied from ExtendedPdpSummary
const Box = styled("div")({
  padding: "16px",
  height: "100%",
  position: "relative",
});
